import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, Repeat, UserPlus, ShoppingBag } from 'lucide-react'
import { platformAPI } from '../../services/api'
import { t, formatDate } from '../../i18n'
import { PageHeader, StatCard, Loading, Empty, SubscriptionChip, TypeChip, Chip, cents } from './ui'

const sumLabel = list => (list?.length ? list.map(x => cents(x.cents, x.currency)).join(' + ') : '—')

export default function PlatformOverview() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    platformAPI.getOverview().then(r => setData(r.data)).catch(e => setError(e.response?.data?.error || 'Failed to load the platform overview'))
  }, [])

  if (error) return <Empty>{t(error)}</Empty>
  if (!data) return <Loading />

  const live = (data.subscriptions.active || 0) + (data.subscriptions.trialing || 0)
  const max = Math.max(1, ...data.signupsByDay.map(d => d.count))
  const staff = Object.entries(data.users).filter(([role]) => role !== 'owner').reduce((s, [, n]) => s + n, 0)

  return (
    <div>
      <PageHeader title={t('Overview')} subtitle={t('Everything happening across the platform.')} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Building2} label={t('Businesses')} value={data.businesses.total}
          hint={t('{v0} active, {v1} suspended', { v0: data.businesses.active, v1: data.businesses.suspended })} />
        <StatCard icon={Repeat} label={t('Monthly recurring revenue')} value={sumLabel(data.mrr)}
          hint={t('{v0} paying or on trial', { v0: live })} />
        <StatCard icon={UserPlus} label={t('New businesses (30 days)')} value={data.businesses.new30d}
          hint={t('{v0} owners, {v1} staff accounts', { v0: data.users.owner || 0, v1: staff })} />
        <StatCard icon={ShoppingBag} label={t('Orders (30 days)')} value={data.orders30d}
          hint={t('{v0} bookings, {v1} customers', { v0: data.bookings30d, v1: data.customers })} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200 lg:col-span-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-slate-900">{t('Sign-ups, last 30 days')}</h2>
            <p className="text-xs text-slate-500">{t('Collected this month')}: {sumLabel(data.collected30d)}</p>
          </div>
          <div className="mt-4 flex h-36 items-end gap-1" dir="ltr">
            {data.signupsByDay.map(d => (
              <div key={d.day} className="group relative flex-1">
                <div className="w-full rounded-t bg-indigo-500/80 group-hover:bg-indigo-600" style={{ height: `${Math.max(d.count ? 8 : 2, (d.count / max) * 136)}px`, opacity: d.count ? 1 : 0.25 }} />
                <span className="pointer-events-none absolute bottom-full start-1/2 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-xs text-white group-hover:block">
                  {formatDate(d.day)}: {d.count}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(data.businesses.byType).map(([type, n]) => <span key={type} className="flex items-center gap-1.5"><TypeChip type={type} /><span className="text-xs text-slate-500">{n}</span></span>)}
          </div>
        </section>

        <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">{t('Subscriptions')}</h2>
          <ul className="mt-3 space-y-2">
            {Object.entries(data.subscriptions).sort((a, b) => b[1] - a[1]).map(([status, n]) => (
              <li key={status} className="flex items-center justify-between"><SubscriptionChip status={status} /><span className="text-sm tabular-nums text-slate-700">{n}</span></li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-xl bg-white ring-1 ring-slate-200">
          <h2 className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-900">{t('Most active (30 days)')}</h2>
          <ul className="divide-y divide-slate-100">
            {data.topBusinesses.map(b => (
              <li key={b.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <Link to={`/platform/businesses?open=${b.id}`} className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900 hover:text-indigo-600">{b.name}</p>
                  <p className="truncate text-xs text-slate-500">{b.owner?.username || t('No owner')}</p>
                </Link>
                <span className="text-sm tabular-nums text-slate-700">{t('{v0} orders', { v0: b.orders_30d })}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-xl bg-white ring-1 ring-slate-200">
          <h2 className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-900">{t('Needs attention')}</h2>
          {data.needsAttention.length ? (
            <ul className="divide-y divide-slate-100">
              {data.needsAttention.map(b => (
                <li key={b.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <Link to={`/platform/businesses?open=${b.id}`} className="truncate text-sm font-medium text-slate-900 hover:text-indigo-600">{b.name}</Link>
                  {b.is_active ? <SubscriptionChip status={b.subscription_status} /> : <Chip tone="red">{t('Suspended')}</Chip>}
                </li>
              ))}
            </ul>
          ) : <p className="px-5 py-8 text-center text-sm text-slate-500">{t('Nothing needs your attention.')}</p>}
        </section>
      </div>
    </div>
  )
}
